import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { calcDatePreset } from '../../components/fields/DateRangePicker';
import DateRangeSelect from '../../components/fields/DateRangeSelect';
import PaginationComponent from '../../components/PaginationComponent';
import QcPassRateCell from './QcPassRateCell';
import { qcResultAPI } from '../../services';
import { SOURCE_KIND_LABELS } from './QcResultsListTab';

const PAGE_SIZE = 20;

export default function QcResultsByEmployeeTab({ branchId, sessionId, onClearSession, onSelectEmployee }) {
  const [range, setRange] = useState(() => calcDatePreset('last3months'));
  const [sortBy, setSortBy] = useState('failed');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0 });
  const [expandedId, setExpandedId] = useState(null);
  // #180900 — mặc định chỉ tính phiên ĐỦ SET đánh giá; bật lên để xem cả phần chưa đủ
  const [includeIncomplete, setIncludeIncomplete] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await qcResultAPI.getQcResultsByEmployee({
        branch_id: branchId || undefined,
        session_id: sessionId || undefined,
        // Đã lọc theo phiên thì bỏ ràng buộc ngày, tránh phiên cũ ra rỗng
        start_date: sessionId ? undefined : range.from,
        end_date: sessionId ? undefined : range.to,
        include_incomplete_sessions: includeIncomplete ? 'true' : undefined,
        sort_by: sortBy,
        page,
        limit: PAGE_SIZE,
      });
      setRows(Array.isArray(res?.data) ? res.data : []);
      setMeta(res?.meta || { total: 0 });
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Không tải được thống kê theo nhân viên');
    } finally {
      setLoading(false);
    }
  }, [branchId, sessionId, range, sortBy, includeIncomplete, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [branchId, sessionId, range, sortBy, includeIncomplete]);

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3">
            {/* #175019 — bỏ ô chọn chi nhánh riêng: chi nhánh chỉ chọn ở thanh trên đầu. */}
            <Col md={3}>
              <Form.Label>Sắp xếp theo</Form.Label>
              <Form.Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="failed">Số lỗi (nhiều nhất)</option>
                <option value="pass_rate">Tỉ lệ đạt (thấp nhất)</option>
                <option value="total">Tổng số (nhiều nhất)</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              <DateRangeSelect fromDate={range.from} toDate={range.to} onChange={(from, to) => setRange({ from, to })} defaultPreset="last3months" />
            </Col>
          </Row>
          {/* #180900 — nói rõ con số đang dựa trên tập nào, và cho lối xem đầy đủ */}
          <div className="mt-3">
            <Form.Check
              type="switch"
              id="qc-include-incomplete-sessions"
              checked={includeIncomplete}
              disabled={!!sessionId}
              onChange={(e) => setIncludeIncomplete(e.target.checked)}
              label={
                sessionId
                  ? 'Đang xem đích danh 1 phiên — hiện đủ số liệu của phiên đó'
                  : `Tính cả phiên chưa đủ ${meta.min_items_for_evaluation || ''} mục (không dùng cho đánh giá)`
              }
            />
            {!sessionId && !includeIncomplete && (
              <div className="text-muted small mt-1">
                Đang tính trên các <strong>phiên đủ set đánh giá</strong> (phiên đã đóng và chấm đủ{' '}
                {meta.min_items_for_evaluation || '—'} mục).
                {meta.excluded_sessions > 0 && (
                  <>
                    {' '}Đã bỏ qua <strong>{meta.excluded_sessions}</strong> phiên chưa đủ set (
                    {meta.excluded_items} mục đã chấm).
                  </>
                )}
              </div>
            )}
          </div>
          {sessionId && (
            <div className="mt-3 d-flex align-items-center gap-2">
              <span className="text-muted small">Đang lọc trong 1 phiên QC được chọn</span>
              {onClearSession && (
                <Button size="sm" variant="outline-secondary" onClick={onClearSession}>
                  Bỏ lọc phiên
                </Button>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-4 text-muted">Không có dữ liệu theo bộ lọc hiện tại.</div>
      ) : (
        <>
          <div className="table-responsive">
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Nhân viên</th>
                  <th>Tổng</th>
                  <th>Đạt</th>
                  <th>Không đạt</th>
                  <th>Chờ</th>
                  <th title="Đạt / (Đạt + Không đạt) — mục còn Chờ chưa chấm không nằm trong phép tính">
                    Tỉ lệ đạt <span className="text-muted fw-normal">(trên số đã chấm)</span>
                  </th>
                  <th>Chưa xử lý</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <React.Fragment key={r.employee.id}>
                    <tr>
                      <td>
                        {r.employee.full_name || r.employee.username}
                        {!r.employee.is_active && <span className="text-muted small ms-1">(đã nghỉ)</span>}
                      </td>
                      <td>{r.total}</td>
                      <td className="text-success">{r.passed}</td>
                      <td className="text-danger">{r.failed}</td>
                      <td>{r.pending + r.in_review}</td>
                      <td style={{ minWidth: 120 }}>
                        <QcPassRateCell
                          passRate={r.pass_rate}
                          passed={r.passed}
                          failed={r.failed}
                          pending={r.pending + r.in_review}
                        />
                      </td>
                      <td>{r.unhandled_failed > 0 ? <span className="text-danger fw-semibold">{r.unhandled_failed}</span> : 0}</td>
                      <td className="d-flex gap-1">
                        <Button size="sm" variant="outline-secondary" onClick={() => setExpandedId(expandedId === r.employee.id ? null : r.employee.id)}>
                          {expandedId === r.employee.id ? 'Ẩn' : 'Chi tiết'}
                        </Button>
                        <Button size="sm" variant="outline-primary" onClick={() => onSelectEmployee(r.employee.id)}>
                          Xem lỗi
                        </Button>
                      </td>
                    </tr>
                    {expandedId === r.employee.id && (
                      <tr>
                        <td colSpan={8} className="bg-light">
                          <div className="d-flex flex-wrap gap-3 py-2">
                            {Object.entries(r.by_source).map(([kind, s]) => (
                              <div key={kind}>
                                <strong>{SOURCE_KIND_LABELS[kind] || kind}:</strong>{' '}
                                <span className="text-success">{s.passed} đạt</span> / <span className="text-danger">{s.failed} không đạt</span> / {s.total} tổng
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </div>
          <PaginationComponent totalItems={meta.total || 0} itemsPerPage={PAGE_SIZE} currentPage={page} onPageChange={setPage} loading={loading} />
        </>
      )}
    </>
  );
}
